import React, { useEffect, useState } from 'react'

import Box from 'components_new/atoms/Box'
import InputBase from 'components_new/atoms/InputBase'
import Text from 'components_new/atoms/Text'

import { useTranslation } from 'language/translator'
import { getScrollbarSx } from 'themes'
import { useTheme } from '@mui/material'

interface EditableTextProps {
  allowTextOverflow?: boolean
  disabled?: boolean
  onSubmit: (value: string) => void
  scaleFactor: number
  textAlign?: 'left' | 'center' | 'right'
  value: string
  variant: 'subtitle' | 'title'
}

const EditableText = (props: EditableTextProps) => {
  const {
    allowTextOverflow,
    disabled,
    onSubmit,
    scaleFactor,
    textAlign,
    value,
    variant
  } = props

  const { t } = useTranslation()

  const [editValue, setEditValue] = useState<string>('')
  const [isActive, setIsActive] = useState<boolean>(false)

  const handleSubmit = () => {
    if (editValue !== value) {
      onSubmit(editValue)
    }
    setIsActive(false)
  }

  useEffect(() => {
    setEditValue(value || t('global', 'untitled'))
  }, [value])

  const color = variant === 'title' ? 'text.primary' : 'text.secondary'
  const fontSize = variant === 'title' ? 20 : 16
  const fontWeight = variant === 'title' ? 'bold' : 'medium'

  const theme = useTheme()

  if (disabled) {
    return (
      <Text
        component={variant === 'title' ? 'h2' : 'p'}
        variant="inherit"
        sx={{
          color: color,
          fontWeight: fontWeight,
          fontSize: fontSize * scaleFactor,
          lineHeight: 1.3,
          flex: 1,
          m: 0,
          p: 0,
          textAlign: textAlign,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: allowTextOverflow ? undefined : '2',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}
      >
        {value || t('global', 'untitled')}
      </Text>
    )
  }

  return (
    <Box
      sx={{ display: 'flex', flex: 1 }}
      onClick={() => {
        if (!isActive && !disabled) {
          setIsActive(true)
        }
      }}
    >
      <InputBase
        fullWidth={true}
        readOnly={!isActive || disabled}
        maxRows={allowTextOverflow ? undefined : 2}
        multiline={true}
        onBlur={() => {
          handleSubmit()
        }}
        onChange={(event) => {
          setEditValue(event.target.value.replace('\n', ''))
        }}
        onKeyUp={(event) => {
          if (event.key === 'Enter') {
            handleSubmit()
          }
        }}
        onMouseDown={(event) => {
          event.stopPropagation()
        }}
        sx={{
          color: color,
          fontWeight: fontWeight,
          fontSize: fontSize * scaleFactor,
          lineHeight: 1.3,
          bgcolor: isActive ? 'action.focus' : undefined,
          p: 0,
          m: 0,
          '& .MuiInputBase-input': {
            p: 0,
            m: 0,
            cursor: disabled ? 'default' : undefined,
            textAlign: textAlign,
            ...getScrollbarSx(theme, scaleFactor),
            // overflow
            ...(!isActive && {
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: '2',
              whiteSpace: 'pre-wrap'
            })
          }
        }}
        value={editValue}
      />
    </Box>
  )
}

export default EditableText
